import { useState, useEffect, useCallback } from "react";
import { BarChart2, Wallet, Download, Loader2, AlertTriangle, Tv } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { publisherApi } from "@/lib/api";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10); };

export default function PublisherDashboard({ fetchDashboard = publisherApi.getDashboard, preview = false }) {
  const [data, setData] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [start, setStart] = useState(monthAgo());
  const [end, setEnd] = useState(today());
  const [channel, setChannel] = useState("all");

  useEffect(() => {
    if (preview) return;
    publisherApi.getChannels().then((r) => setChannels(r.data)).catch(() => {});
  }, [preview]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: start, end_date: end };
      if (channel !== "all") params.channel = channel;
      const res = await fetchDashboard(params);
      setData(res.data);
      if (preview && channel === "all") setChannels((res.data.channels || []).map((c) => c.channel));
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [start, end, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const params = { start_date: start, end_date: end };
      if (channel !== "all") params.channel = channel;
      const res = await api.get(publisherApi.exportUrl, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${start}_${end}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const s = data?.summary || {};
  const hasPending = (s.pending || 0) > 0;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="publisher-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {data?.cp_name || "Publisher"} Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Your channels' performance · {data?.rev_share_pct ?? "—"}% revenue share</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-[150px] text-sm" data-testid="pub-start-date" />
          <span className="text-slate-400 text-sm">→</span>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-[150px] text-sm" data-testid="pub-end-date" />
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="pub-channel-filter"><SelectValue placeholder="All Channels" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              {channels.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {!preview && (
            <Button onClick={downloadCsv} disabled={downloading} variant="outline" className="h-9" data-testid="pub-download-csv">
              {downloading ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} className="mr-1.5" />} CSV
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={26} /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="kpi-card" data-testid="pub-kpi-impressions">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center"><BarChart2 size={20} className="text-purple-600" strokeWidth={1.5} /></div>
                <span className="kpi-label">Total Impressions</span>
              </div>
              <div className="kpi-value text-purple-700">{(s.impressions || 0).toLocaleString()}</div>
            </div>
            <div className="kpi-card" data-testid="pub-kpi-earnings">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Wallet size={20} className="text-emerald-600" strokeWidth={1.5} /></div>
                <span className="kpi-label">Your Earnings</span>
              </div>
              <div className="kpi-value text-emerald-700">${(s.earnings || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            </div>
          </div>

          {hasPending && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5" data-testid="pub-pending-warning">
              <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">Some earnings are pending the ad-server cost setup; they will appear once finance configures the cost.</p>
            </div>
          )}

          {data?.daily?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Daily Earnings</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.daily} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="earn" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#059669" stopOpacity={0.3} /><stop offset="95%" stopColor="#059669" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="ymd" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <Tooltip />
                  <Area type="monotone" dataKey="earnings" stroke="#059669" fill="url(#earn)" name="Your Earnings" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <Tv size={15} className="text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-900">By Channel</h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase py-3 px-4">Channel</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase py-3 px-4 text-right">Impressions</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase py-3 px-4 text-right">Your Earnings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.channels || []).length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center py-12 text-slate-400">No data for this range. (Note: requires the Aniview publisher macro to be populated.)</TableCell></TableRow>
                ) : (
                  data.channels.map((c, i) => (
                    <TableRow key={i} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`pub-channel-row-${i}`}>
                      <TableCell className="py-2.5 px-4 text-sm text-slate-700">{c.channel}{c.pending && <span className="ml-2 text-[10px] text-amber-600">pending cost</span>}</TableCell>
                      <TableCell className="py-2.5 px-4 text-sm text-right font-mono text-slate-700">{c.impressions.toLocaleString()}</TableCell>
                      <TableCell className="py-2.5 px-4 text-sm text-right font-mono font-semibold text-emerald-700">${c.earnings.toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
